import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Loader2, Trash2, Crown } from 'lucide-react'
import { QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS } from '@/lib/menu-chrome'
import { useAuth } from '@/providers/auth-provider'
import { useWorkspace } from '@/providers/workspace-provider'
import { supabase } from '@/lib/supabase'
import {
  fetchAccountEmails,
  fetchAccountProfile,
  patchAccountProfile,
  postAccountEmail,
  deleteAccountEmail,
  putAccountEmailPreferredOrg,
  putAccountEmailPromote,
  formatAccountEmailError,
  type AccountEmailRow,
} from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { SettingsSection, SettingsSectionRow } from '@/components/features/settings/settings-section'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/auth/password-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { HostedMcpAgentsTip } from '@/components/features/settings/hosted-mcp-agents-tip'

async function tryRefreshAuthSession() {
  if (!supabase) return
  try {
    const { error } = await supabase.auth.refreshSession()
    if (error) return
  } catch {
    // Optional after profile updates; invite-only sessions may lack a refresh token.
  }
}

const NO_ORG_VALUE = '__none__'

const changePasswordSchema = z
  .object({
    newPassword: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const addEmailSchema = z.object({
  email: z.string().email('Enter a valid email address'),
})

const displayNameSchema = z.object({
  fullName: z.string().max(120, 'Display name is too long'),
})

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>
type AddEmailFormValues = z.infer<typeof addEmailSchema>
type DisplayNameFormValues = z.infer<typeof displayNameSchema>

type AccountSettingsProps = {
  focusSection?: string | null
}

export function AccountSettings({ focusSection }: AccountSettingsProps) {
  const { user, isAuthConfigured } = useAuth()
  const { workspaces } = useWorkspace()
  const [changing, setChanging] = useState(false)
  const [emailsLoading, setEmailsLoading] = useState(false)
  const [profileLoading, setProfileLoading] = useState(false)
  const [savingDisplayName, setSavingDisplayName] = useState(false)
  const [needsPassword, setNeedsPassword] = useState(false)
  const [secondaryEmails, setSecondaryEmails] = useState<AccountEmailRow[]>([])
  const [primaryDisplay, setPrimaryDisplay] = useState<string | null>(null)
  const passwordCardRef = useRef<HTMLDivElement>(null)

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  const addEmailForm = useForm<AddEmailFormValues>({
    resolver: zodResolver(addEmailSchema),
    defaultValues: { email: '' },
  })

  const displayNameForm = useForm<DisplayNameFormValues>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: { fullName: '' },
  })

  const loadProfile = useCallback(async () => {
    if (!isAuthConfigured) return
    setProfileLoading(true)
    try {
      const data = await fetchAccountProfile()
      displayNameForm.reset({ fullName: data.fullName ?? '' })
      setNeedsPassword(Boolean(data.needsPassword))
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load profile'
      toast.error(msg)
    } finally {
      setProfileLoading(false)
    }
  }, [displayNameForm, isAuthConfigured])

  const loadEmails = useCallback(async () => {
    if (!isAuthConfigured) return
    setEmailsLoading(true)
    try {
      const data = await fetchAccountEmails()
      setPrimaryDisplay(data.primary?.email ?? null)
      setSecondaryEmails(data.emails ?? [])
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load emails'
      toast.error(msg)
    } finally {
      setEmailsLoading(false)
    }
  }, [isAuthConfigured])

  useEffect(() => {
    void loadProfile()
    void loadEmails()
  }, [loadProfile, loadEmails])

  useEffect(() => {
    if (focusSection !== 'password' || profileLoading) return
    passwordCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusSection, profileLoading])

  const onSubmitDisplayName = async (values: DisplayNameFormValues) => {
    setSavingDisplayName(true)
    try {
      const data = await patchAccountProfile(values.fullName)
      displayNameForm.reset({ fullName: data.fullName ?? '' })
      await tryRefreshAuthSession()
      window.dispatchEvent(new CustomEvent('flownotes:account-profile-updated'))
      toast.success('Display name updated.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Request failed')
    } finally {
      setSavingDisplayName(false)
    }
  }

  const onSubmitPassword = async (values: ChangePasswordFormValues) => {
    if (!supabase) return
    setChanging(true)
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword,
        data: { password_set_at: new Date().toISOString() },
      })
      if (error) {
        toast.error(error.message)
        return
      }
      setNeedsPassword(false)
      window.dispatchEvent(new CustomEvent('flownotes:password-set'))
      toast.success(needsPassword ? 'Password set. You can sign in with email and password.' : 'Password updated.')
      form.reset({ newPassword: '', confirmPassword: '' })
    } finally {
      setChanging(false)
    }
  }

  const onAddEmail = async (values: AddEmailFormValues) => {
    addEmailForm.clearErrors('email')
    try {
      const res = await postAccountEmail(values.email.trim())
      toast.success('Verification sent. Check your inbox (or server logs in development).')
      if (res.verifyUrl) {
        toast.message('Dev link', { description: res.verifyUrl })
      }
      addEmailForm.reset({ email: '' })
      await loadEmails()
    } catch (e) {
      const message =
        e instanceof Error ? e.message : formatAccountEmailError('request_failed')
      addEmailForm.setError('email', { type: 'server', message })
    }
  }

  const onRemoveSecondary = async (id: string) => {
    try {
      await deleteAccountEmail(id)
      toast.success('Email removed.')
      await loadEmails()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Request failed')
    }
  }

  const onPreferredOrgChange = async (rowId: string, value: string) => {
    const orgId = value === NO_ORG_VALUE ? null : value
    try {
      await putAccountEmailPreferredOrg(rowId, orgId)
      await loadEmails()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Request failed')
    }
  }

  const onPromote = async (id: string) => {
    try {
      await putAccountEmailPromote(id)
      toast.success('Primary email updated.')
      await tryRefreshAuthSession()
      await loadEmails()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Request failed')
    }
  }

  if (!isAuthConfigured) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Sign in is not configured. Account settings are available when using Supabase auth.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid={testIds.accountSettings.section}>
      <p className="text-sm text-muted-foreground">
        Your display name, sign-in email, additional verified addresses, and password.
      </p>
      {user?.email && (
        <SettingsSectionRow>
          <p className="text-sm">
            <span className="text-muted-foreground">Session email </span>
            <span className="font-medium">{user.email}</span>
          </p>
        </SettingsSectionRow>
      )}

      <HostedMcpAgentsTip />

      <SettingsSection
        data-testid={testIds.accountSettings.profileCard}
        title="Profile"
        description="Shown on workspace member lists, document attribution, and invites."
      >
        {profileLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <Form {...displayNameForm}>
            <form
              onSubmit={displayNameForm.handleSubmit(onSubmitDisplayName)}
              className="flex flex-col gap-3 sm:flex-row sm:items-end max-w-lg"
            >
              <FormField
                control={displayNameForm.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Display name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your name"
                        autoComplete="name"
                        data-testid={testIds.accountSettings.displayNameInput}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={savingDisplayName || !displayNameForm.formState.isDirty}
                data-testid={testIds.accountSettings.displayNameSaveButton}
              >
                {savingDisplayName ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </form>
          </Form>
        )}
      </SettingsSection>

      <SettingsSection
        data-testid={testIds.accountSettings.emailAddressesCard}
        title="Email addresses"
        description="Your primary address is used to sign in. Add others for invites or workspace identity; verify before you can make one primary."
      >
        <div className="space-y-4">
          {emailsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <ul className="space-y-3">
              <li>
                <SettingsSectionRow className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="font-medium truncate text-sm">
                      {primaryDisplay ?? user?.email ?? '—'}
                    </span>
                    <Badge variant="outline" className="text-xs font-medium text-muted-foreground">
                      Primary
                    </Badge>
                    <Badge variant="outline" className="border-green-500/30 text-green-600 text-xs">
                      Verified
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sign-in email for this Maater account.
                  </p>
                </SettingsSectionRow>
              </li>

              {secondaryEmails.map((row) => (
                <li key={row.id}>
                  <SettingsSectionRow
                    data-testid={testIds.accountSettings.secondaryEmailRow(row.id)}
                    className="space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium truncate text-sm">{row.email}</span>
                          {row.verified ? (
                            <Badge
                              variant="outline"
                              className="border-green-500/30 text-green-600 text-xs"
                            >
                              Verified
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="border-amber-500/30 text-amber-700 text-xs"
                            >
                              Pending
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {row.verified ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => void onPromote(row.id)}
                            title="Make this the primary sign-in email"
                          >
                            <Crown className="h-3.5 w-3.5 mr-1.5" />
                            Make primary
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn('h-8 w-8', QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS)}
                          onClick={() => void onRemoveSecondary(row.id)}
                          title="Remove email"
                          aria-label={`Remove ${row.email}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5 max-w-md">
                      <label className="text-xs font-medium text-muted-foreground">
                        Linked workspace
                      </label>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        This is the address Maater uses for you in that workspace: review, readers,
                        and notifications. Leave None only when the address is not tied to one workspace.
                      </p>
                      <Select
                        value={row.preferredOrgId ?? NO_ORG_VALUE}
                        onValueChange={(v) => void onPreferredOrgChange(row.id, v)}
                        disabled={!row.verified}
                      >
                        <SelectTrigger className="h-8 text-xs w-full sm:w-[280px]">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_ORG_VALUE}>None</SelectItem>
                          {workspaces.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!row.verified ? (
                        <p className="text-[11px] text-muted-foreground">
                          Available after you verify this address.
                        </p>
                      ) : null}
                    </div>
                  </SettingsSectionRow>
                </li>
              ))}
            </ul>
          )}

          <Form {...addEmailForm}>
            <form
              onSubmit={addEmailForm.handleSubmit(onAddEmail)}
              className="flex flex-col gap-2 pt-1 border-t border-border/60"
            >
              <FormField
                control={addEmailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="pt-3">
                    <FormLabel>Add email</FormLabel>
                    <FormDescription>
                      We send a verification link. You can set preferred workspace after it verifies.
                    </FormDescription>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end max-w-lg">
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@other-domain.com"
                          autoComplete="email"
                          className="flex-1"
                          data-testid={testIds.accountSettings.addEmailInput}
                          {...field}
                        />
                      </FormControl>
                      <Button type="submit" data-testid={testIds.accountSettings.addEmailButton}>
                        Send verification
                      </Button>
                    </div>
                    <FormMessage data-testid={testIds.accountSettings.addEmailError} />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
      </SettingsSection>

      <SettingsSection
        ref={passwordCardRef}
        data-testid={testIds.accountSettings.passwordCard}
        className={cn(focusSection === 'password' && 'ring-2 ring-primary/40')}
        title={needsPassword ? 'Set your password' : 'Change password'}
        description={
          needsPassword
            ? 'You signed in via invite. Set a password so you can sign in again after this session.'
            : 'Set a new password. You will stay signed in.'
        }
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmitPassword)} className="space-y-4 max-w-sm">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{needsPassword ? 'Password' : 'New password'}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="••••••••"
                      autoComplete="new-password"
                      data-testid={testIds.accountSettings.passwordInput}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="••••••••"
                      autoComplete="new-password"
                      data-testid={testIds.accountSettings.passwordConfirmInput}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={changing} data-testid={testIds.accountSettings.passwordSubmitButton}>
              {changing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {needsPassword ? 'Set password' : 'Change password'}
            </Button>
          </form>
        </Form>
      </SettingsSection>
    </div>
  )
}
